<template>
    <USelectMenu
        v-model="account"
        :items="filteredAccounts"
        virtualize
        option-attribute="value"
        by="value"
        placeholder="Velg konto"
        class="w-[300px] h-8"
        @update:model-value="onAccountSelected"
        @update:search-term="accountFilter = $event"
    >
        <template v-if="account" #trailing>
            <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-ph-x"
                @click.stop="onClearAccount"
            />
        </template>
        <template #item="{ item }">
            <div class="flex items-center justify-between w-full">
                <div class="flex gap-2 overflow-hidden" :title="item.name">
                    <span class="font-semibold">{{ item.accountNo }}</span>
                    <span
                        class="truncate"
                        :class="{ 'font-semibold': item?.accountNo == account?.accountNo }"
                    >
                        {{ item.name }}
                    </span>
                </div>
            </div>
        </template>
    </USelectMenu>
</template>

<script setup lang="ts">
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {computed, ref, watch} from 'vue'
import {SelectMenuItem} from '@nuxt/ui'

const props = defineProps({
    accountNo: {type: String},
})
const emit = defineEmits(['on-account-no-selected'])

type AccountSelectMenuItem = SelectMenuItem & {
    accountNo: string
    name: string
}

const account = ref<AccountSelectMenuItem | undefined>(undefined)
const accountFilter = ref('')

const toItem = (ba: BookkeepingAccount): AccountSelectMenuItem => ({
    value: ba.accountNo,
    label: `${ba.accountNo} - ${ba.name}`,  // needed for search to work
    type: 'item',
    accountNo: ba.accountNo,
    name: ba.name
})

const filteredAccounts = computed<AccountSelectMenuItem[]>(() => {
    const search = accountFilter.value?.trim().toLowerCase()
    const base = [...bookkeepingAccounts.value].sort(
        (a, b) => Number(a.accountNo) - Number(b.accountNo)
    )

    if (!search) return base.map(toItem)

    const prefixMatches: BookkeepingAccount[] = []
    const otherMatches: BookkeepingAccount[] = []

    for (const ba of base) {
        const accountNo = (ba.accountNo ?? '').toLowerCase()
        const name = (ba.name ?? '').toLowerCase()

        if (accountNo.startsWith(search) || name.startsWith(search)) {
            prefixMatches.push(ba)
        } else if (accountNo.includes(search) || name.includes(search)) {
            otherMatches.push(ba)
        }
    }

    return [...prefixMatches, ...otherMatches].map(toItem)
})

const onAccountSelected = (selectedAccount: AccountSelectMenuItem) => {
    account.value = selectedAccount
    emit('on-account-no-selected', selectedAccount.accountNo)
}

const onClearAccount = () => {
    account.value = undefined
    accountFilter.value = ''
    emit('on-account-no-selected', undefined)
}

watch(() => props.accountNo, (newAccountNo) => {
    if (newAccountNo !== account.value?.accountNo) {
        const ba = bookkeepingAccounts.value.find(ba => ba.accountNo === newAccountNo)
        account.value = ba ? toItem(ba) : undefined
    }
}, { immediate: true })

</script>

<style scoped>

</style>